// Reference-number parsing — the routing key for this entire app.
//
// The applicant portals mint two series (submission-service.ts:337 and
// admin-jobs.ts:262):
//   * MDBS-2027-0001 / MDGS-2027-0001  — a real, submitted application.
//   * MDBS-2027-I-0001                 — an INCOMPLETE draft converted after
//                                        the deadline. Never claimable.
//
// This app is dual-program, so the MDBS-/MDGS- prefix is what decides which
// tracker a claim is written to. Everything downstream depends on getting this
// right, so parsing is deliberately strict about SHAPE while forgiving about
// the transcription noise a real sponsor produces.

export type Program = "boys" | "girls";
export type ProgramShort = "MDBS" | "MDGS";

export interface ParsedReference {
  /** Exactly as the user typed it, for echoing back in errors/logs. */
  raw: string;
  /** Canonical form, e.g. "MDBS-2027-0347". Use this as the lookup key. */
  normalized: string;
  program: Program;
  programShort: ProgramShort;
  sessionYear: number;
  sequence: number;
  /**
   * True for the I-series. The reference is well-formed but belongs to an
   * application that was never completed — callers MUST refuse to claim it
   * rather than treating it as a miss, so the sponsor can be told something
   * useful instead of "no match".
   */
  incomplete: boolean;
}

const SHORT_TO_PROGRAM: Record<ProgramShort, Program> = {
  MDBS: "boys",
  MDGS: "girls",
};

// Unicode dashes that show up when a sponsor copies the reference number out of
// the printed Step 6 sponsor copy — that document is a PDF, and PDF text
// extraction routinely turns a plain hyphen into an en-dash or a non-breaking
// hyphen. Folding these is not cosmetic: without it the primary,
// most-encouraged path in the Sponsor Guide fails for a user who did exactly
// what the guide told them to do.
const DASHES = /[‐‑‒–—―−﹘﹣－]/g;

const PATTERN = /^(MDBS|MDGS)-(\d{4})-(I-)?(\d{4})$/;

/**
 * Parses a reference number, tolerating case, surrounding/internal whitespace
 * and PDF-mangled dashes. Returns null when the shape is not a reference
 * number at all.
 *
 * Deliberately NOT tolerant of a wrong digit count — a 3- or 5-digit sequence
 * is a different number, not a typo to be guessed at.
 */
export function parseReferenceNumber(input: string): ParsedReference | null {
  const raw = String(input ?? "");

  const normalized = raw
    .normalize("NFKC")
    .replace(DASHES, "-")
    .toUpperCase()
    // Collapse whitespace anywhere, including around the separators, so
    // "MDBS - 2027 - 0347" and "mdbs 2027 0347" both land.
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s*-\s*/g, "-")
    // A sponsor reading aloud or retyping may use spaces as the separator.
    .replace(/ /g, "-");

  const m = PATTERN.exec(normalized);
  if (!m) return null;

  const programShort = m[1] as ProgramShort;
  return {
    raw,
    normalized,
    program: SHORT_TO_PROGRAM[programShort],
    programShort,
    sessionYear: Number(m[2]),
    sequence: Number(m[4]),
    incomplete: Boolean(m[3]),
  };
}

/**
 * Convenience for the common case: a claimable reference, or null.
 * Folds "not a reference number" and "incomplete series" into one miss —
 * only use where the caller genuinely does not need to tell them apart.
 */
export function parseClaimableReference(input: string): ParsedReference | null {
  const parsed = parseReferenceNumber(input);
  return parsed && !parsed.incomplete ? parsed : null;
}
